/**
 * Per-customer report delivery destinations — /api/admin/report-destinations*.
 *
 * Platform-only server-side. A destination row holds a THIRD PARTY's server
 * credentials and decides where that customer's billing data is sent, so it is
 * not delegated to customer-company admins.
 *
 * The secret is write-only across this boundary: the list response carries
 * `has_secret` and never the password, and an omitted/blank secret on save means
 * "keep the stored one". The adapter enforces the read side too — the list query
 * doesn't select the column at all.
 */
import { apiFetch } from "./apiClient";

export type DestinationKind = "ftp";
export type ReportKind = "consignment_weekly";
export type DestinationFormat = "json" | "csv" | "xlsx";

/** Transport settings for kind="ftp". Never includes the password. */
export interface FtpDestinationConfig {
  host?: string;
  port?: number;
  username?: string;
  remote_dir?: string;
  /** Explicit TLS (AUTH TLS) on the normal port. Defaults true server-side. */
  use_tls?: boolean;
  passive?: boolean;
  timeout?: number;
  /** Placeholders: {doc_num} {stem} {kind} {smartbox} {ext} */
  filename_pattern?: string;
}

export interface ReportDestination {
  destination_id: number;
  company_id: number;
  company_name: string | null;
  name: string;
  kind: DestinationKind;
  report_kind: ReportKind;
  format: DestinationFormat;
  config: FtpDestinationConfig;
  enabled: boolean;
  /** True when a password is stored. The value itself never comes back. */
  has_secret: boolean;
  last_success_utc: string | null;
  last_failure_utc: string | null;
  last_error: string | null;
  consecutive_failures: number;
  created_by: string | null;
  created_utc: string | null;
}

export interface DestinationsResponse {
  destinations: ReportDestination[];
  /** Server-declared vocabularies, so the form never offers what this build
   *  can't actually deliver. */
  kinds: DestinationKind[];
  report_kinds: ReportKind[];
  formats: DestinationFormat[];
}

export interface DeliveryRow {
  delivery_id: number;
  report_kind: string;
  item_key: string | null;
  remote_path: string | null;
  byte_count: number | null;
  success: boolean;
  error: string | null;
  attempted_utc: string | null;
}

export interface TestResult {
  success: boolean;
  remote_path: string;
  /** The server's own error text — "530 Login incorrect" is the whole point. */
  detail: string;
}

export interface CreateDestinationInput {
  company_id: number;
  name: string;
  kind: DestinationKind;
  report_kind: ReportKind;
  format: DestinationFormat;
  config: FtpDestinationConfig;
  secret?: string;
  enabled: boolean;
}

/** Every field optional — only what's present is changed. `kind` is not
 *  editable: it would reinterpret the stored secret against another server. */
export interface UpdateDestinationInput {
  name?: string;
  format?: DestinationFormat;
  config?: FtpDestinationConfig;
  /** Omit or "" to keep the stored password. */
  secret?: string;
  enabled?: boolean;
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, token, init);
}

export function listDestinations(
  token: string,
  companyId?: number,
): Promise<DestinationsResponse> {
  const qs = companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
  return req<DestinationsResponse>(token, `/api/admin/report-destinations${qs}`);
}

export function createDestination(
  token: string,
  input: CreateDestinationInput,
): Promise<{ destination_id: number }> {
  return req(token, "/api/admin/report-destinations", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateDestination(
  token: string,
  destinationId: number,
  input: UpdateDestinationInput,
): Promise<{ updated: boolean }> {
  return req(token, `/api/admin/report-destinations/${destinationId}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function deleteDestination(
  token: string,
  destinationId: number,
): Promise<{ deleted: boolean }> {
  return req(token, `/api/admin/report-destinations/${destinationId}`, {
    method: "DELETE",
  });
}

/**
 * Connect + authenticate + cwd, writing nothing.
 *
 * Pass `secret` to prove a NEW password before saving it; that value is used
 * for the one connection and never stored, so a failed test leaves no broken
 * credential behind. Omit it to verify the stored one still works.
 */
export function testDestination(
  token: string,
  destinationId: number,
  secret?: string,
): Promise<TestResult> {
  return req(token, `/api/admin/report-destinations/${destinationId}/test`, {
    method: "POST",
    body: JSON.stringify(secret ? { secret } : {}),
  });
}

export function listDeliveries(
  token: string,
  destinationId: number,
  limit = 50,
): Promise<{ deliveries: DeliveryRow[] }> {
  return req(
    token,
    `/api/admin/report-destinations/${destinationId}/deliveries?limit=${limit}`,
  );
}
